// Pure text helpers for the template content editor: what the preview renders, what the editor warns
// about, and what the Tab key does. Kept out of the component so they can be unit-tested
// (src/__tests__).
//
// Templates are filled in by the backend with two syntaxes, both handled here:
//   - single placeholders, {{Key}} or [[Key]] (Tp_Communication PlaceholderTools)
//   - section tags, {{#if Key}}…{{/if}} and {{#each Key}}…{{/each}}, which the backend resolves with
//     StringTools.EvaluateConditionalSection / FillSectionWithCollectionData
// The section tags are the dangerous half: those helpers look the opening and closing tag up by
// text, and when either is missing they return an empty section — so a mistyped closing tag makes
// the whole block disappear from the sent email with nothing logged. That is what
// findSectionIssues() is for, and why it is worth showing while the file is being edited.

/** Both double-delimiter syntaxes, and with them the section tags (a section tag is one too). */
const DoublePlaceholderSource = /\{\{[^{}]*\}\}|\[\[[^[\]]*\]\]/.source;

/**
 * The single-delimiter forms, {Key} and [Key], which templates use as placeholders just as often
 * ({Target} in the redirect page, [No Value] where an answer was left blank). Letters, digits and
 * spaces only, which is what keeps a CSS rule body ({margin:0;padding:0}) or a JavaScript block from
 * being read as one — those always carry a colon, a semicolon or a brace. A bracketed aside in prose
 * can still be marked by mistake; that costs nothing, since marking is a preview overlay and never
 * touches the stored text.
 */
// The dash is escaped only where it is followed by the space: there it would otherwise read as a
// range. Last in the class it needs no escape, and escaping it anyway trips no-useless-escape.
const SinglePlaceholderSource = /\{[A-Za-z0-9_.\- ]*[A-Za-z0-9_.-]\}|\[[A-Za-z0-9_.\- ]*[A-Za-z0-9_.-]\]/.source;

/** Any placeholder, longest delimiter first so {{Key}} is never read as { {Key} }. */
const PlaceholderPattern = new RegExp(`(${DoublePlaceholderSource}|${SinglePlaceholderSource})`, 'g');

/** An opening or closing section tag, with the keyword captured. */
const SectionTagPattern = /\{\{\s*(#if|#each|\/if|\/each)\b[^{}]*\}\}/g;

/**
 * Splits HTML into the parts that must be passed through untouched and the text between them: a whole
 * <style>/<script> element first (its body is code, and a marker span inside a stylesheet would break
 * it — all the more so now that a single brace counts as a placeholder), then any single tag.
 */
const PassThroughPattern = /(<(?:style|script)\b[^>]*>[\s\S]*?<\/(?:style|script)\s*>|<[^>]*>)/gi;

const SectionKeywords = {
  ifOpen: '#if',
  ifClose: '/if',
  eachOpen: '#each',
  eachClose: '/each',
};

type SectionTag = {
  keyword: string;
  text: string;
  line: number;
};

/** The 1-based line an index falls on, for a message the user can act on. */
function lineOfIndex(text: string, index: number): number {
  let line = 1;
  for (let i = 0; i < index && i < text.length; i++) {
    if (text[i] === '\n') {
      line++;
    }
  }
  return line;
}

function readSectionTags(text: string): SectionTag[] {
  const tags: SectionTag[] = [];
  const pattern = new RegExp(SectionTagPattern.source, 'g');
  let match = pattern.exec(text);

  while (match !== null) {
    tags.push({
      keyword: match[1].toLowerCase(),
      text: match[0].trim(),
      line: lineOfIndex(text, match.index),
    });
    match = pattern.exec(text);
  }

  return tags;
}

function closingKeywordOf(openingKeyword: string): string {
  return openingKeyword === SectionKeywords.ifOpen ? SectionKeywords.ifClose : SectionKeywords.eachClose;
}

function openingKeywordOf(closingKeyword: string): string {
  return closingKeyword === SectionKeywords.ifClose ? SectionKeywords.ifOpen : SectionKeywords.eachOpen;
}

function isOpeningKeyword(keyword: string): boolean {
  return keyword === SectionKeywords.ifOpen || keyword === SectionKeywords.eachOpen;
}

/**
 * Every unbalanced section tag, as sentences to show the user. Empty when the file is sound.
 * Nesting is checked with a stack, so a closing tag that belongs to the wrong opening one is
 * reported as well as a missing or surplus one.
 */
export function findSectionIssues(text: string): string[] {
  if (!text) {
    return [];
  }

  const issues: string[] = [];
  const openTags: SectionTag[] = [];

  readSectionTags(text).forEach((tag) => {
    if (isOpeningKeyword(tag.keyword)) {
      openTags.push(tag);
      return;
    }

    const openTag = openTags.pop();

    if (openTag === undefined) {
      issues.push(`Line ${tag.line}: ${tag.text} has no matching {{${openingKeywordOf(tag.keyword)} …}} before it.`);
      return;
    }

    if (closingKeywordOf(openTag.keyword) !== tag.keyword) {
      issues.push(
        `Line ${tag.line}: ${tag.text} closes ${openTag.text} from line ${openTag.line},` +
          ` which needs {{${closingKeywordOf(openTag.keyword)}}} instead.`
      );
      // The opening tag is spent either way; reporting it again as unclosed would be noise.
    }
  });

  openTags.forEach((openTag) => {
    issues.push(`Line ${openTag.line}: ${openTag.text} is never closed with {{${closingKeywordOf(openTag.keyword)}}}.`);
  });

  return issues;
}

/** Every placeholder and section tag used in the file, de-duplicated, in the order they appear. */
export function listPlaceholders(text: string): string[] {
  if (!text) {
    return [];
  }

  const found: string[] = [];
  const pattern = new RegExp(PlaceholderPattern.source, 'g');
  let match = pattern.exec(text);

  while (match !== null) {
    const placeholder = match[0].trim();
    if (!found.includes(placeholder)) {
      found.push(placeholder);
    }
    match = pattern.exec(text);
  }

  return found;
}

function isSectionTag(placeholder: string): boolean {
  return new RegExp(`^${SectionTagPattern.source}$`).test(placeholder);
}

/**
 * Wraps every placeholder in a marker span so the preview shows what is filled in later. Only the
 * text between tags is touched: a placeholder inside an attribute (href="{{PageUrl}}") has to stay
 * exactly as it is, or the markup around it breaks.
 */
export function highlightPlaceholders(text: string): string {
  return text
    .split(PassThroughPattern)
    .map((segment) =>
      segment === undefined || segment.startsWith('<')
        ? segment ?? ''
        : segment.replace(PlaceholderPattern, (placeholder) => {
            const className = isSectionTag(placeholder) ? 'tplsection' : 'tplkey';
            return `<span class="${className}">${placeholder}</span>`;
          })
    )
    .join('');
}

// Prepended, not merged into the template's own <head>: the parser hoists a leading <style> into the
// head by itself, and a template is not guaranteed to have a head to merge into. The class names are
// deliberately odd so they cannot collide with the template's own styles.
//
// The marker is drawn INSIDE the marked box (outline-offset, and nothing that takes width): an
// outline drawn outside it eats into the gap between two placeholders that sit next to each other,
// which reads as the space between them having disappeared.
const HighlightStyle =
  '<style>' +
  '.tplkey,.tplsection{outline-offset:-1px;border-radius:2px;}' +
  '.tplkey{background:#fff3cd;outline:1px dotted #a97a00;}' +
  '.tplsection{background:#d1e7dd;outline:1px dotted #0f5132;font-style:italic;}' +
  '</style>';

/**
 * The document to hand to the preview frame. The template's own markup is passed through untouched
 * (this is a preview of the stored file, not a cleaned-up version of it) — with the placeholder
 * markers added when they are switched on.
 */
export function buildPreviewDocument(text: string, highlight: boolean): string {
  const content = text ?? '';
  return highlight ? HighlightStyle + highlightPlaceholders(content) : content;
}

// ---------------------------------------------------------------------------------------------
// Tab in the editor
//
// A textarea hands Tab to the browser, which moves the focus out of it - useless in a box holding
// indented markup. The component swallows the key and applies the edit below instead: one tab, or a
// whole selected block indented or outdented at once. Kept here as a pure value so the rules are
// testable without a DOM.
// ---------------------------------------------------------------------------------------------

/** What Tab inserts. A stored template is indented with spaces, but a tab is what the key means. */
export const IndentCharacter = '\t';

/** How many leading spaces Shift+Tab takes off a line that is indented with spaces. */
export const SpacesPerIndent = 4;

/** A replacement to apply to the editor, and where the selection ends up afterwards. */
export type TextEdit = {
  from: number;
  to: number;
  replacement: string;
  selectionStart: number;
  selectionEnd: number;
};

function lineStartIndex(value: string, index: number): number {
  if (index <= 0) {
    return 0;
  }
  return value.lastIndexOf('\n', index - 1) + 1;
}

function lineEndIndex(value: string, index: number): number {
  const lineBreak = value.indexOf('\n', index);
  return lineBreak === -1 ? value.length : lineBreak;
}

/** How much indentation the line starts with, in characters: one tab, or up to SpacesPerIndent spaces. */
function indentWidthOf(line: string): number {
  if (line.startsWith(IndentCharacter)) {
    return 1;
  }

  let spaces = 0;
  while (spaces < SpacesPerIndent && line[spaces] === ' ') {
    spaces++;
  }
  return spaces;
}

function outdentLine(line: string): string {
  return line.slice(indentWidthOf(line));
}

/**
 * The edit the Tab key makes, or null when there is nothing to do (Shift+Tab on a line that is not
 * indented). A selection spanning more than one line is indented or outdented line by line and stays
 * selected, so the key can be pressed again.
 */
export function buildTabEdit(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  outdent: boolean
): TextEdit | null {
  const content = value ?? '';
  const spansLines = content.slice(selectionStart, selectionEnd).includes('\n');

  if (spansLines) {
    const from = lineStartIndex(content, selectionStart);
    const to = lineEndIndex(content, selectionEnd);
    const replacement = content
      .slice(from, to)
      .split('\n')
      // An empty line gets no indentation - trailing whitespace nobody asked for
      .map((line) => (outdent ? outdentLine(line) : line.length === 0 ? line : IndentCharacter + line))
      .join('\n');

    if (replacement === content.slice(from, to)) {
      return null;
    }

    return { from, to, replacement, selectionStart: from, selectionEnd: from + replacement.length };
  }

  if (!outdent) {
    const caret = selectionStart + IndentCharacter.length;
    return {
      from: selectionStart,
      to: selectionEnd,
      replacement: IndentCharacter,
      selectionStart: caret,
      selectionEnd: caret,
    };
  }

  const lineStart = lineStartIndex(content, selectionStart);
  const removed = indentWidthOf(content.slice(lineStart, lineEndIndex(content, selectionStart)));

  if (removed === 0) {
    return null;
  }

  return {
    from: lineStart,
    to: lineStart + removed,
    replacement: '',
    selectionStart: Math.max(lineStart, selectionStart - removed),
    selectionEnd: Math.max(lineStart, selectionEnd - removed),
  };
}
